/** @file Dopesheet time axis: frame <-> x pixels, frame snapping, zoom to cursor, pan, fit, visible range. */
import { clamp } from '@pose2d/math/clamp';

/** Horizontal mapping of the timeline: frame shown at x = 0 and pixels per frame. */
export interface TimelineScale {
  frame0: number;
  pxPerFrame: number;
}

const MIN_PX_PER_FRAME = 0.5;
const MAX_PX_PER_FRAME = 120;

/** Time-axis math; x is in canvas pixels from the left edge of the key area. */
export const timelineScale = {
  /** Smallest pixels-per-frame any operation produces. */
  MIN_PX_PER_FRAME,
  /** Largest pixels-per-frame any operation produces. */
  MAX_PX_PER_FRAME,
  /** x pixel of a (possibly fractional) frame. */
  frameToX(s: TimelineScale, frame: number): number {
    return (frame - s.frame0) * s.pxPerFrame;
  },
  /** Fractional frame at x pixels. */
  xToFrame(s: TimelineScale, x: number): number {
    return s.frame0 + x / s.pxPerFrame;
  },
  /** Whole frame nearest to x pixels (scrub and key drags land on frames). */
  snapFrame(s: TimelineScale, x: number): number {
    return Math.round(s.frame0 + x / s.pxPerFrame);
  },
  /** Multiplies pixels-per-frame by factor (clamped) keeping the frame under x in place. */
  zoomAt(s: TimelineScale, x: number, factor: number): TimelineScale {
    const pxPerFrame = clamp(s.pxPerFrame * factor, MIN_PX_PER_FRAME, MAX_PX_PER_FRAME);
    return { frame0: s.frame0 + x / s.pxPerFrame - x / pxPerFrame, pxPerFrame };
  },
  /** Moves the keys by dx pixels, as when the time axis is dragged. */
  pan(s: TimelineScale, dx: number): TimelineScale {
    return { frame0: s.frame0 - dx / s.pxPerFrame, pxPerFrame: s.pxPerFrame };
  },
  /** Shows frames first..last across width pixels with pad pixels on both sides (at least one frame wide). */
  fit(first: number, last: number, width: number, pad: number): TimelineScale {
    const pxPerFrame = clamp(Math.max(width - 2 * pad, 1) / Math.max(last - first, 1), MIN_PX_PER_FRAME, MAX_PX_PER_FRAME);
    return { frame0: first - pad / pxPerFrame, pxPerFrame };
  },
  /** Whole frames [first, last] that touch a canvas width pixels wide. */
  visibleFrames(s: TimelineScale, width: number): [number, number] {
    return [Math.floor(s.frame0), Math.ceil(s.frame0 + width / s.pxPerFrame)];
  },
};
